// The profile comes first (02-DESIGN/03-console/01-the-console.md § no
// backend; 03-signing-in.md § the profile comes first): the first thing the
// page does is read the platform profile at its own origin, and it refuses to
// start without one rather than come up pointing at another environment.
// Everything it shows or calls derives from that profile and from the tenant
// profile it reads next. The readers are the library's; this module adds the
// refusal and the shape check.

import { identity } from "@impire-io/sdk";

export type PlatformProfile = identity.PlatformProfile;
export type TenantProfile = identity.TenantProfile;
export type Provider = identity.Provider;

/** BootRefusal is why the page did not start. */
export class BootRefusal extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "BootRefusal";
  }
}

/** ReadOptions are the library's: the fetch to use, a signal. */
export type ReadOptions = identity.ReadOptions;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * checkPlatformProfile refuses a profile that cannot stand up a console: it
 * needs the websocket URL, the sentinel and a platform provider with a lane.
 */
export function checkPlatformProfile(p: unknown): PlatformProfile {
  if (!isRecord(p)) {
    throw new BootRefusal("the platform profile is not an object");
  }
  if (typeof p.websocket_url !== "string" || p.websocket_url === "") {
    throw new BootRefusal("the platform profile names no websocket URL");
  }
  if (typeof p.sentinel !== "string" || p.sentinel === "") {
    throw new BootRefusal("the platform profile carries no sentinel");
  }
  if (!isRecord(p.platform_provider) || typeof p.platform_provider.lane !== "string") {
    throw new BootRefusal("the platform profile names no platform provider");
  }
  const lane = p.platform_provider.lane;
  if (lane !== "oidc" && lane !== "github") {
    throw new BootRefusal(`the platform provider's lane "${lane}" is not one a browser signs in on`);
  }
  return p as unknown as PlatformProfile;
}

/**
 * readPlatform reads the platform profile at the origin and refuses to go on
 * without one. It is the first request the page makes.
 */
export async function readPlatform(origin: string, opts?: ReadOptions): Promise<PlatformProfile> {
  let raw: unknown;
  try {
    raw = await identity.readPlatformProfile(origin, opts);
  } catch (err) {
    throw new BootRefusal(
      `no platform profile at ${origin}: ${err instanceof Error ? err.message : String(err)}`,
      { cause: err },
    );
  }
  return checkPlatformProfile(raw);
}

/** readTenant reads one tenant's profile; a missing one is reported as such. */
export async function readTenant(origin: string, slug: string, opts?: ReadOptions): Promise<TenantProfile> {
  let raw: unknown;
  try {
    raw = await identity.readTenantProfile(origin, slug, opts);
  } catch (err) {
    throw new BootRefusal(
      `no tenant "${slug}" at ${origin}: ${err instanceof Error ? err.message : String(err)}`,
      {
        cause: err,
      },
    );
  }
  if (!isRecord(raw) || typeof raw.slug !== "string" || typeof raw.account !== "string") {
    throw new BootRefusal(`the profile of "${slug}" names no slug and account`);
  }
  if (raw.slug !== slug) {
    throw new BootRefusal(`the profile at "${slug}" is "${raw.slug}"'s`);
  }
  return raw as unknown as TenantProfile;
}

/**
 * Surface is where a provider configuration lets people sign in, as
 * impire-sdk-go/identity/profile.go names them: the browser, the terminal,
 * and a desktop AI client through the gateway's OAuth face
 * (02-DESIGN/09-gateway/02-the-oauth-face.md § the authorization server is
 * the provider, restated).
 */
export const Surface = {
  browser: "browser",
  terminal: "terminal",
  desktop: "desktop",
} as const;
export type Surface = (typeof Surface)[keyof typeof Surface];

/** The shape of a provider that says where it may sign people in, and under which client ids. */
interface Surfaced {
  client_id?: string;
  client_ids?: Record<string, string>;
  surfaces?: string[];
}

/**
 * allows is Provider.Allows (impire-sdk-go/identity/profile.go): a
 * configuration naming no surfaces allows every one.
 */
export function allows(p: Surfaced, surface: Surface): boolean {
  const s = p.surfaces ?? [];
  return s.length === 0 || s.includes(surface);
}

/**
 * nativeClientId is Provider.TerminalClientID
 * (impire-sdk-go/identity/terminal.go): the terminal's own client id where
 * the provider forces separate ones, the configuration's client id
 * otherwise. A desktop AI client signs in as the same native client, so the
 * loopback redirect is added there (02-DESIGN/09-gateway/02-the-oauth-face.md
 * § the one-line add, and the bootstrap).
 */
export function nativeClientId(p: Surfaced): string {
  const own = p.client_ids?.[Surface.terminal];
  return own !== undefined && own !== "" ? own : (p.client_id ?? "");
}
